import { Button } from '@/components/ui/button';
import { AbsatzVorgabe } from '@/domain/controlling/entities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { faCircle } from '@fortawesome/pro-regular-svg-icons';
import {
  faCircle as faCircleSolid,
  faCheck,
} from '@fortawesome/pro-solid-svg-icons';
import { isPlanwertValid } from '../helpers';
import { useTagebuchEinstellungen } from '@/domain/controlling/hooks/useTagebuchEinstellungen';
import { useDialogContext } from '../DialogContext';
import { useNavigate } from 'react-router-dom';
import { useRouteParams } from '../useRouteParams';
import { useTranslation } from 'react-i18next';
import { useFormSubmit } from '../useFormSubmit';
import { Planwert } from '@/domain/controlling/entities/planwert';
import { useStandortContext } from '@/features/standortContext/StandortContext';

// eslint-disable-next-line max-lines-per-function
export function Day({
  currentDate,
  selectedDate,
  currentPlanwert,
  absatzVorgabe,
}: {
  currentDate: Date;
  selectedDate: Date;
  currentPlanwert: Planwert;
  absatzVorgabe: AbsatzVorgabe;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const { umsatzplanungAnzeige } = useTagebuchEinstellungen(standortId);
  const isDateEqualToSelectedDate = dayjs(currentDate).isSame(
    selectedDate,
    'date',
  );

  const { dispatch, planwerteForm } = useDialogContext();
  const { routeParams } = useRouteParams();

  function onSuccess() {
    dispatch({ type: 'reset' });
    dispatch({
      type: 'updateDialogMessage',
      dialogMessage: {
        message: t('domain.controlling.umsatzplanung.planung.success'),
        type: 'success',
      },
    });
    const navigateTo = dayjs(currentDate).format('YYYY-MM-DD');
    navigate(
      `/controlling/umsatzplanung/${routeParams.jahr}/${routeParams.monat}/planwerte/edit?datum=${navigateTo}`,
    );
  }

  function onError(message?: string) {
    dispatch({
      type: 'updateDialogMessage',
      dialogMessage: {
        message:
          message ??
          t('domain.controlling.umsatzplanung.planung.errors.unknownError'),
        type: 'error',
      },
    });
  }

  const { handleSubmit } = useFormSubmit(onSuccess, onError);

  return (
    <div className="flex flex-col gap-1 justify-center items-center">
      <Button
        disabled={planwerteForm.isSubmitting}
        variant="ghost"
        className={`h-9 w-9 font-normal hover:font-semibold ${
          isDateEqualToSelectedDate
            ? 'bg-primary-blue-B600 text-white hover:bg-primary-blue-B600'
            : ''
        }`}
        onClick={(e) => {
          if (planwerteForm.hasChanges) {
            handleSubmit(e);
          } else {
            dispatch({ type: 'reset' });
            const navigateTo = dayjs(currentDate).format('YYYY-MM-DD');
            navigate(
              `/controlling/umsatzplanung/${routeParams.jahr}/${routeParams.monat}/planwerte/edit?datum=${navigateTo}`,
            );
          }
        }}
        id={`${currentDate}-day`}>
        {currentDate.getDate()}
      </Button>
      {isPlanwertValid(umsatzplanungAnzeige, absatzVorgabe, currentPlanwert) ? (
        <div className="fa-layers fa-fw">
          <FontAwesomeIcon
            icon={faCircleSolid}
            className="text-neutrals-N200"
          />
          <FontAwesomeIcon
            icon={faCheck}
            className="text-primary-green-G500"
            transform="shrink-6"
          />
        </div>
      ) : (
        <FontAwesomeIcon icon={faCircle} className="text-neutrals-N300" />
      )}
    </div>
  );
}
